import { addDays, addMonths, make, parts, type ISODate } from './date';
import { active, type Slice } from './slices';
import { euro } from './format';
import type { RentChangeKind } from './enums';

/**
 * Rent and prepayment changes of a tenancy (German tenancy law). Pure rules shared by server (validation, letter)
 * and web (form hints). Nothing here gives legal advice: it computes statutory dates and flags what to check.
 *
 * - agreement       § 557 Abs. 1 BGB: both parties agree on new amounts; applied as agreed.
 * - increase_558    § 558 BGB: request for consent up to the local comparative rent. Only the net cold rent; only effective
 *                   with the tenant's consent, from the start of the third calendar month after receipt (§ 558b Abs. 1).
 * - prepayment_560  § 560 Abs. 4 BGB: one-sided adjustment of the prepayments after a statement, by declaration in text form.
 * - graduated_557a  § 557a BGB: the next step of a graduated rent agreed in the contract (no declaration needed).
 * - index_557b      § 557b BGB: index rent, changed by declaration in text form, effective from the start of the month after
 *                   next after receipt (§ 557b Abs. 3); the rent must have stayed unchanged for at least one year.
 */

/** Which amounts a kind may change. */
export const RENT_CHANGE_FIELDS: Record<RentChangeKind, { rent: boolean; prepayments: boolean }> = {
  agreement: { rent: true, prepayments: true },
  increase_558: { rent: true, prepayments: false },
  prepayment_560: { rent: false, prepayments: true },
  graduated_557a: { rent: true, prepayments: false },
  index_557b: { rent: true, prepayments: false },
};

/** Kinds that only take effect once the tenant consented (status `pending_consent` until then). */
export const needsConsent = (kind: RentChangeKind) => kind === 'increase_558';

/** First day of the month `n` months after the month of `d`. */
export const firstOfMonthPlus = (d: ISODate, n: number): ISODate => {
  const p = parts(addMonths(make(parts(d).y, parts(d).m, 1), n));
  return make(p.y, p.m, 1);
};

/** Last day of the month `n` months after the month of `d`. */
export const lastOfMonthPlus = (d: ISODate, n: number): ISODate => addDays(firstOfMonthPlus(d, n + 1), -1);

/**
 * Earliest date the change can take effect when the letter reaches the tenant on `receivedOn`:
 * § 558b Abs. 1 (start of the 3rd calendar month after receipt), § 557b Abs. 3 (start of the month after next).
 * For § 560 Abs. 4 the law names no fixed date; like the prepayment dialog we suggest the start of the month after next.
 * Agreements and graduated rent follow the contract (null).
 */
export function earliestEffectiveDate(kind: RentChangeKind, receivedOn: ISODate): ISODate | null {
  switch (kind) {
    case 'increase_558':
      return firstOfMonthPlus(receivedOn, 3);
    case 'index_557b':
    case 'prepayment_560':
      return firstOfMonthPlus(receivedOn, 2);
    default:
      return null;
  }
}

/** § 558b Abs. 2: the tenant may consider the request until the end of the 2nd calendar month after receipt. */
export const consentDeadline = (receivedOn: ISODate): ISODate => lastOfMonthPlus(receivedOn, 2);

/** New rent of an index rent: old rent × new index / old index, rounded to whole cents. */
export function indexedRentCents(rentCents: number, baseIndex: number, currentIndex: number): number {
  if (!(baseIndex > 0) || !(currentIndex > 0)) return rentCents;
  return Math.round((rentCents * currentIndex) / baseIndex);
}

/** Change in percent with one decimal (for letters and hints). */
export const changePct = (before: number, after: number) =>
  before > 0 ? Math.round(((after - before) / before) * 1000) / 10 : 0;

/** Start of the rent value in force on `day` (the last rent change or the start of the tenancy). */
export function rentSince(rentSlices: readonly Slice<number>[], day: ISODate): ISODate | null {
  const s = active(rentSlices).find((x) => x.validFrom <= day && (x.validTo === null || x.validTo >= day));
  return s?.validFrom ?? null;
}

export type RentCheckCode =
  | 'tooEarly'
  | 'notAnIncrease'
  | 'lockPeriod'
  | 'requestTooSoon'
  | 'capExceeded'
  | 'indexTooSoon'
  | 'noStatement'
  | 'statementNotDelivered'
  | 'aboveSuggestion'
  | 'retroactive'
  | 'outsideTenancy'
  | 'noChange';
/** `error` blocks saving (statutory date or nonsense input), `warning` asks the landlord to check. */
export type RentCheck = {
  code: RentCheckCode;
  severity: 'error' | 'warning';
  params?: Record<string, string | number>;
};

export type RentCheckInput = {
  kind: RentChangeKind;
  /** Date of the letter = assumed receipt by the tenant. */
  declaredOn: ISODate;
  effectiveDate: ISODate;
  tenancy: { start: ISODate; end: ISODate | null; rentCold: readonly Slice<number>[] };
  before: {
    rentColdCents: number | null;
    prepaymentOpsCents: number | null;
    prepaymentHeatCents: number | null;
  };
  next: { rentColdCents?: number; prepaymentOpsCents?: number; prepaymentHeatCents?: number };
  /** Kappungsgrenze in percent: 20, or 15 where the state set a lower cap (§ 558 Abs. 3 Satz 2). */
  cappingPct?: number;
  /** A final statement exists for this tenancy (basis of a § 560 adjustment). */
  hasStatement?: boolean;
  /** That statement reached this tenancy (sent, `deliveredAt`); § 560 Abs. 4 needs a statement the tenant received. */
  statementDelivered?: boolean;
  /** Suggested new prepayments from that statement (ADR-0019); a higher amount is only a warning. */
  suggestion?: { prepaymentOpsCents: number; prepaymentHeatCents: number } | null;
};

/** Statutory dates and plausibility of a planned change; see the kinds above. */
export function rentChecks(i: RentCheckInput): RentCheck[] {
  const out: RentCheck[] = [];
  const changed =
    (i.next.rentColdCents !== undefined && i.next.rentColdCents !== i.before.rentColdCents) ||
    (i.next.prepaymentOpsCents !== undefined && i.next.prepaymentOpsCents !== i.before.prepaymentOpsCents) ||
    (i.next.prepaymentHeatCents !== undefined && i.next.prepaymentHeatCents !== i.before.prepaymentHeatCents);
  if (!changed) out.push({ code: 'noChange', severity: 'error' });
  if (i.effectiveDate < i.tenancy.start || (i.tenancy.end && i.effectiveDate > i.tenancy.end)) {
    out.push({
      code: 'outsideTenancy',
      severity: 'error',
      params: { start: i.tenancy.start, end: i.tenancy.end ?? '' },
    });
  }
  const earliest = earliestEffectiveDate(i.kind, i.declaredOn);
  if (earliest && i.effectiveDate < earliest) {
    // § 558b / § 557b fix the date by law; for § 560 it is a recommendation only
    out.push({
      code: 'tooEarly',
      severity: i.kind === 'prepayment_560' ? 'warning' : 'error',
      params: { date: earliest },
    });
  }
  if (!earliest && i.effectiveDate < i.declaredOn)
    out.push({ code: 'retroactive', severity: 'warning', params: { date: i.effectiveDate } });

  const before = i.before.rentColdCents ?? 0;
  const since = rentSince(i.tenancy.rentCold, addDays(i.effectiveDate, -1)) ?? i.tenancy.start;
  if (i.kind === 'increase_558') {
    const next = i.next.rentColdCents ?? before;
    if (next <= before) out.push({ code: 'notAnIncrease', severity: 'error' });
    // § 558 Abs. 1: unchanged for 15 months when the increase takes effect; request at the earliest one year after the last increase
    const lock = addMonths(since, 15);
    if (i.effectiveDate < lock)
      out.push({ code: 'lockPeriod', severity: 'warning', params: { since, date: lock } });
    const request = addMonths(since, 12);
    if (i.declaredOn < request)
      out.push({ code: 'requestTooSoon', severity: 'warning', params: { since, date: request } });
    // § 558 Abs. 3: at most +20 % (or 15 %) within three years; base = rent three years before the new rent takes effect
    const pct = i.cappingPct ?? 20;
    const baseDay = addMonths(i.effectiveDate, -36);
    const base = valueOn(i.tenancy.rentCold, baseDay < i.tenancy.start ? i.tenancy.start : baseDay) ?? before;
    const max = Math.floor((base * (100 + pct)) / 100);
    if (next > max) out.push({ code: 'capExceeded', severity: 'warning', params: { pct, max, base } });
  }
  if (i.kind === 'index_557b') {
    // § 557b Abs. 2: the rent must stay unchanged for at least one year (apart from §§ 559–560)
    const minDay = addMonths(since, 12);
    if (i.effectiveDate < minDay)
      out.push({ code: 'indexTooSoon', severity: 'warning', params: { since, date: minDay } });
  }
  if (i.kind === 'prepayment_560') {
    // § 560 Abs. 4 BGB: only after a statement, and only one the tenant received (BGH VIII ZR 294/10: a formally
    // correct statement is the basis); an amount above the suggestion is allowed but needs a reason
    if (!i.hasStatement) out.push({ code: 'noStatement', severity: 'error' });
    else if (i.statementDelivered === false) out.push({ code: 'statementNotDelivered', severity: 'error' });
    const s = i.suggestion;
    if (s) {
      const ops = i.next.prepaymentOpsCents;
      const heat = i.next.prepaymentHeatCents;
      if ((ops !== undefined && ops > s.prepaymentOpsCents) || (heat !== undefined && heat > s.prepaymentHeatCents))
        out.push({
          code: 'aboveSuggestion',
          severity: 'warning',
          // formatted here: the clients format only `max`/`base` and dates
          params: { ops: euro(s.prepaymentOpsCents), heat: euro(s.prepaymentHeatCents) },
        });
    }
  }
  return out;
}

function valueOn(list: readonly Slice<number>[], day: ISODate): number | undefined {
  return active(list).find((s) => s.validFrom <= day && (s.validTo === null || s.validTo >= day))?.value;
}

/** Monthly total of rent and prepayments (missing parts count as 0). */
export const monthlyTotal = (x: {
  rentColdCents?: number | null;
  prepaymentOpsCents?: number | null;
  prepaymentHeatCents?: number | null;
}) => (x.rentColdCents ?? 0) + (x.prepaymentOpsCents ?? 0) + (x.prepaymentHeatCents ?? 0);

/**
 * Suggested new monthly prepayments of a statement recipient (ADR-0019): the engine's operating and heating parts.
 * Results frozen before ENGINE 1.2.0 only have the total; it is split like the current prepayments (heating share by
 * the ratio of the current heating prepayment), as before.
 */
export function prepaymentSuggestion(
  rec: {
    suggestedMonthlyPrepaymentCents?: number | null;
    suggestedMonthlyPrepaymentOpsCents?: number | null;
    suggestedMonthlyPrepaymentHeatCents?: number | null;
  },
  current: { prepaymentOpsCents?: number | null; prepaymentHeatCents?: number | null },
): { prepaymentCents: number; prepaymentOpsCents: number; prepaymentHeatCents: number } | null {
  const total = rec.suggestedMonthlyPrepaymentCents;
  if (total == null) return null;
  if (rec.suggestedMonthlyPrepaymentOpsCents != null && rec.suggestedMonthlyPrepaymentHeatCents != null)
    return {
      prepaymentCents: total,
      prepaymentOpsCents: rec.suggestedMonthlyPrepaymentOpsCents,
      prepaymentHeatCents: rec.suggestedMonthlyPrepaymentHeatCents,
    };
  const ops = current.prepaymentOpsCents ?? 0;
  const heat = current.prepaymentHeatCents ?? 0;
  const heatShare = heat > 0 ? Math.round((total * heat) / (ops + heat)) : 0;
  return { prepaymentCents: total, prepaymentOpsCents: total - heatShare, prepaymentHeatCents: heatShare };
}
